import { calendarDateKey } from './components/mobileCalendarCounts';
import useMobileSchedule from '../../hooks/useMobileSchedule';
import MobileScheduleMoveDialog from './components/MobileScheduleMoveDialog';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { FiTool, FiRefreshCw } from 'react-icons/fi';
import BackButton from '../common/BackButton';
import Toast from '../common/Toast';

import ProductionTable from './components/ProductionTable';
import ProductionReadyJobs from './components/ProductionReadyJobs';
import EditAssignment from '../assignments/EditAssignment';

import {
    getProductionScheduleRows,
    getProductionReadyJobs,
    addToProductionSchedule,
    updateProductionPhase,
    subscribeToProductionSchedule,
    unsubscribeFromProductionSchedule,
} from '../../lib/supabase/production-schedule';

// ───────────────── LocalStorage Helpers ─────────────────
const loadPref = (key, fallback) => {
    try {
        const v = localStorage.getItem(key);
        return v !== null ? JSON.parse(v) : fallback;
    } catch {
        return fallback;
    }
};

const savePref = (key, value) => {
    try { localStorage.setItem(key, JSON.stringify(value)); } catch { }
};

// ───────────────── Page ─────────────────
const ProductionSchedule = () => {
    const { company, userProfile } = useAuth();
    const isMobile = useMobileSchedule();
    const [mobileProductionJob, setMobileProductionJob] = useState(null);
    const navigate = useNavigate();
    const { companySlug } = useParams();

    // ─── EDIT MODAL ─────────────────
    const [editOpen, setEditOpen] = useState(false);
    const [editJobId, setEditJobId] = useState(null);

    // ─── TABLE STATE ─────────────────
    const [tableRows, setTableRows] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);

    // ─── READY STATE ─────────────────
    const [readyJobs, setReadyJobs] = useState([]);
    const [readyLoading, setReadyLoading] = useState(false);
    const [readyRefreshing, setReadyRefreshing] = useState(false);

    // ─── DRAG ─────────────────
    const [isDragOver, setIsDragOver] = useState(false);
    const dragJobRef = useRef(null);

    // Always-current refs so subscription handlers never close over stale callbacks
    const fetchReadyJobsRef = useRef(null);
    const fetchTableRowsRef = useRef(null);
    const silentlySyncProductionTableRef = useRef(null);

    const readyFetchInFlightRef = useRef(false);
    const readyFetchQueuedRef = useRef(false);
    const readyFetchTimerRef = useRef(null);


    // ─── TABLE FILTERS ─────────────────
    const [tableSearch, setTableSearch] = useState('');
    const [tableQuoteType, setTableQuoteType] = useState(loadPref('ps_table_quoteType', null));
    const [tableSortBy, setTableSortBy] = useState(loadPref('ps_table_sortBy', 'sort_order'));
    const [tableJobTypeFilter, setTableJobTypeFilter] = useState(loadPref('ps_table_jobType', 'all'));
    const [tableUrgentFilter, setTableUrgentFilter] = useState(loadPref('ps_table_urgent', 'all'));
    const [tableHideCompleted, setTableHideCompleted] = useState(loadPref('ps_table_hideCompleted', false));

    // ─── READY FILTERS ─────────────────
    const [readySearch, setReadySearch] = useState('');
    const [readyQuoteType, setReadyQuoteType] = useState(loadPref('ps_ready_quoteType', null));
    const [readySortBy, setReadySortBy] = useState(loadPref('ps_ready_sortBy', 'newest'));
    const [readyUrgentFilter, setReadyUrgentFilter] = useState(loadPref('ps_ready_urgent', 'all'));

    // ─── Persist Preferences ─────────────────
    useEffect(() => savePref('ps_table_quoteType', tableQuoteType), [tableQuoteType]);
    useEffect(() => savePref('ps_table_sortBy', tableSortBy), [tableSortBy]);
    useEffect(() => savePref('ps_table_jobType', tableJobTypeFilter), [tableJobTypeFilter]);
    useEffect(() => savePref('ps_table_urgent', tableUrgentFilter), [tableUrgentFilter]);
    useEffect(() => savePref('ps_table_hideCompleted', tableHideCompleted), [tableHideCompleted]);
    useEffect(() => savePref('ps_ready_quoteType', readyQuoteType), [readyQuoteType]);
    useEffect(() => savePref('ps_ready_sortBy', readySortBy), [readySortBy]);
    useEffect(() => savePref('ps_ready_urgent', readyUrgentFilter), [readyUrgentFilter]);

    // ───────────────── TABLE FETCH ─────────────────
    const fetchTableRows = useCallback(async () => {
        if (!company?.id) return;

        try {
            setTableLoading(true);
            const result = await getProductionScheduleRows(company.id, {
                sortBy: tableSortBy,
                jobTypeFilter: tableJobTypeFilter,
                urgentFilter: tableUrgentFilter,
                quoteTypeFilter: tableQuoteType,
                searchQuery: tableSearch,
                hideCompleted: tableHideCompleted
            });
            setTableRows(result.data);
        } catch (err) {
            Toast.error('Failed to load production schedule', { position: 'bottom-right' });
        } finally {
            setTableLoading(false);
        }
    }, [
        company?.id,
        tableSortBy,
        tableJobTypeFilter,
        tableUrgentFilter,
        tableQuoteType,
        tableSearch,
        tableHideCompleted
    ]);

    // ───────────────── READY FETCH ─────────────────
    const fetchReadyJobs = useCallback(async (silent = false) => {
        if (!company?.id) return;

        // prevent 5-20 overlapping Supabase calls
        if (readyFetchInFlightRef.current) {
            readyFetchQueuedRef.current = true;
            return;
        }

        clearTimeout(readyFetchTimerRef.current);

        readyFetchTimerRef.current = setTimeout(async () => {
            readyFetchInFlightRef.current = true;

            try {
                if (!silent) setReadyLoading(true);
                else setReadyRefreshing(true);

                const result = await getProductionReadyJobs(company.id, {
                    sortBy: readySortBy,
                    urgentFilter: readyUrgentFilter,
                    quoteTypeFilter: readyQuoteType,
                    searchQuery: readySearch,
                });

                setReadyJobs(result.data || []);
            } catch (error) {
                console.error('fetchReadyJobs failed:', error);
                Toast.error('Failed to load ready jobs', { position: 'bottom-right' });
            } finally {
                readyFetchInFlightRef.current = false;
                setReadyLoading(false);
                setReadyRefreshing(false);

                if (readyFetchQueuedRef.current) {
                    readyFetchQueuedRef.current = false;
                    fetchReadyJobs(true);
                }
            }
        }, silent ? 250 : 0);
    }, [
        company?.id,
        readySortBy,
        readyUrgentFilter,
        readyQuoteType,
        readySearch
    ]);

    // ───────────────── EFFECTS ─────────────────
    useEffect(() => {
        fetchTableRows();
    }, [
        company?.id,
        tableSortBy,
        tableJobTypeFilter,
        tableUrgentFilter,
        tableQuoteType,
        tableSearch,
        tableHideCompleted
    ]);

    useEffect(() => {
        fetchReadyJobs();
    }, [
        company?.id,
        readySortBy,
        readyUrgentFilter,
        readyQuoteType,
        readySearch
    ]);




    const tableRefreshTimerRef = useRef(null);


    const shallowSameRow = (a, b) => {
        if (a === b) return true;
        if (!a || !b) return false;

        const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
        for (const key of keys) {
            if (a[key] !== b[key]) return false;
        }
        return true;
    };

    const mergeTableRowsWithoutReload = useCallback((serverRows) => {
        setTableRows(prev => {
            const prevById = new Map(prev.map(row => [row.id, row]));

            return serverRows.map(serverRow => {
                const existing = prevById.get(serverRow.id);
                return existing && shallowSameRow(existing, serverRow)
                    ? existing
                    : { ...existing, ...serverRow };
            });
        });
    }, []);

    const silentlySyncProductionTable = useCallback(() => {
        if (!company?.id) return;

        clearTimeout(tableRefreshTimerRef.current);

        tableRefreshTimerRef.current = setTimeout(async () => {
            try {
                const result = await getProductionScheduleRows(company.id, {
                    sortBy: tableSortBy,
                    jobTypeFilter: tableJobTypeFilter,
                    urgentFilter: tableUrgentFilter,
                    quoteTypeFilter: tableQuoteType,
                    searchQuery: tableSearch,
                    hideCompleted: tableHideCompleted,
                });

                mergeTableRowsWithoutReload(result.data || []);
            } catch (error) {
                console.error('Silent production table sync failed:', error);
            }
        }, 120);
    }, [
        company?.id,
        tableSortBy,
        tableJobTypeFilter,
        tableUrgentFilter,
        tableQuoteType,
        tableSearch,
        tableHideCompleted,
        mergeTableRowsWithoutReload,
    ]);


    // Keep refs in sync so the subscription handler always calls the latest versions
    fetchReadyJobsRef.current = fetchReadyJobs;
    fetchTableRowsRef.current = fetchTableRows;
    silentlySyncProductionTableRef.current = silentlySyncProductionTable;

    // ───────────────── REALTIME ─────────────────
    useEffect(() => {
        if (!company?.id) return;

        const channel = subscribeToProductionSchedule(company.id, (payload) => {
            const { table, eventType, new: newData, old: oldData } = payload;

            // if (table === 'job_production') {
            //     if (eventType === 'INSERT') {
            //         fetchTableRowsRef.current();
            //         fetchReadyJobsRef.current(true);
            //     }

            //     if (eventType === 'UPDATE') {
            //         setTableRows(prev =>
            //             prev.map(r => r.id === newData.id ? {
            //                 ...r,
            //                 ...newData,
            //                 // remap snake_case → camelCase fields that the table reads
            //                 sortOrder: newData.sort_order ?? r.sortOrder,
            //             } : r)
            //         );
            //     }

            //     if (eventType === 'DELETE') {
            //         setTableRows(prev => prev.filter(r => r.id !== oldData.id));
            //         fetchReadyJobsRef.current(true);
            //     }
            // }

            if (table === 'job_production') {
                if (eventType === 'INSERT') {
                    silentlySyncProductionTableRef.current?.();
                    // fetchReadyJobsRef.current(true);
                    return;
                }

                if (eventType === 'UPDATE') {
                    silentlySyncProductionTableRef.current?.();
                    return;
                }

                if (eventType === 'DELETE') {
                    setTableRows(prev => prev.filter(r => r.id !== oldData.id));
                    fetchReadyJobsRef.current(true);
                    return;
                }
            }
            if (table === 'jobs' && eventType === 'UPDATE') {
                if (newData?.is_cancelled === true) {
                    setTableRows(prev =>
                        prev.filter(r => r.jobId !== newData.id)
                    );

                    setReadyJobs(prev =>
                        prev.filter(r => r.jobId !== newData.id)
                    );

                    return;
                }

                silentlySyncProductionTableRef.current?.();
                fetchReadyJobsRef.current?.(true);
                return;
            }

            if (table === 'job_quotes' && eventType === 'UPDATE') {
                const quoteDisabled =
                    newData.is_required === false ||
                    newData.layout_only === true;

                if (quoteDisabled) {
                    setTableRows(prev =>
                        prev.filter(r =>
                            r.quoteId
                                ? r.quoteId !== newData.id
                                : !(r.jobId === newData.job_id && r.quoteType === newData.quote_type)
                        )
                    );

                    setReadyJobs(prev =>
                        prev.filter(r =>
                            r.quoteId
                                ? r.quoteId !== newData.id
                                : !(r.jobId === newData.job_id && r.quoteType === newData.quote_type)
                        )
                    );

                    return;
                }

                const quoteEnabled =
                    newData.is_required === true &&
                    newData.layout_only !== true;

                if (quoteEnabled) {
                    silentlySyncProductionTableRef.current?.();
                    fetchReadyJobsRef.current(true);

                    setTableRows(prev =>
                        prev.map(r =>
                            r.quoteId === newData.id ||
                                (!r.quoteId && r.jobId === newData.job_id && r.quoteType === newData.quote_type)
                                ? {
                                    ...r,
                                    deliveryTime: newData.delivery_time_slot || '',
                                    quoteDeliveryDate: newData.expected_delivery_date || r.quoteDeliveryDate || null,
                                }
                                : r
                        )
                    );

                    return;
                }

                // Any other job_quotes change (sent_production, design_status, layout_only off, etc.)
                // — always silently refetch the ready list so it reflects the true DB state.
                // Also patch the production table rows in-place for field changes.
                fetchReadyJobsRef.current(true);
                setTableRows(prev =>
                    prev.map(r =>
                        r.quoteId === newData.id
                            ? { ...r, deliveryTime: newData.delivery_time_slot || '' }
                            : r
                    )
                );
            }
        });

        // return () => unsubscribeFromProductionSchedule(channel);
        return () => {
            clearTimeout(tableRefreshTimerRef.current);
            clearTimeout(readyFetchTimerRef.current);
            unsubscribeFromProductionSchedule(channel);
        };
    }, [company?.id]);

    // ───────────────── NAVIGATION ─────────────────
    const handleOpenAssignment = useCallback((assignmentId) => {
        window.open(`/${company?.slug || companySlug}/assignment/${assignmentId}`, '_blank');
    }, [company?.slug, companySlug]);

    const handleEditAssignment = useCallback((assignmentId) => {
        setEditJobId(assignmentId);
        setEditOpen(true);
    }, []);

    // ───────────────── PHASE UPDATE ─────────────────
    const handleStartProduction = useCallback(async (rowId, action) => {
        try {
            await updateProductionPhase(rowId, action);

            const phaseMap = {
                start_cutting: 'in_cutting',
                end_cutting: 'cutting_ended',
                start_production: 'in_production',
                end_production: 'completed',
            };

            setTableRows(prev =>
                prev.map(r =>
                    r.id === rowId
                        ? { ...r, phase: phaseMap[action] || r.phase }
                        : r
                )
            );
        } catch {
            Toast.error('Failed to update production phase', { position: 'bottom-right' });
        }
    }, []);

    // ───────────────── DRAG ─────────────────
    const handleDragStart = useCallback((e, job) => {
        dragJobRef.current = job;
        e.dataTransfer.setData('text/plain', JSON.stringify(job));
    }, []);

    const handleDrop = useCallback(async (e, selectedJob = null, scheduledDate = null) => {
        e.preventDefault();
        setIsDragOver(false);

        let job = selectedJob || dragJobRef.current;
        if (!job) {
            try {
                job = JSON.parse(e.dataTransfer.getData('text/plain'));
            } catch {
                return;
            }
        }

        if (!job?.jobId || !job?.quoteId || !company?.id) return;

        try {
            const newRow = await addToProductionSchedule(company.id, {
                jobId: job.jobId,
                quoteType: job.quoteType,
                jobQuoteId: job.quoteId,
                ...(scheduledDate ? { scheduledDate: scheduledDate.toISOString().slice(0, 10) } : {}),
            }, userProfile);

            setTableRows(prev =>
                prev.some(r => r.id === newRow.id)
                    ? prev
                    : [...prev, newRow]
            );

            setReadyJobs(prev =>
                prev.filter(j => j.quoteId !== job.quoteId)
            );

            Toast.success(`${job.uniqueId} (${job.quoteType}) added`, { position: 'bottom-right' });

        } catch {
            Toast.error('Failed to add to schedule', { position: 'bottom-right' });
        }

        dragJobRef.current = null;

    }, [company?.id, userProfile]);

    // ───────────────── RENDER ─────────────────
    return (
        <div className="production-page flex flex-col gap-6 pb-10">

            <div className="production-header flex flex-col gap-3">
                <BackButton
                    text="Back to Schedule Hub"
                    variant="ghost"
                    size="sm"
                    iconType="chevron"
                    className="text-blue-600 hover:text-blue-800 self-start"
                />

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="p-3 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl shadow-lg">
                            <FiTool className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h1 className="text-3xl font-bold text-gray-900">Production Schedule</h1>
                            <p className="text-gray-500 text-sm mt-0.5">
                                Drag jobs from the panel below into the schedule table
                            </p>
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            fetchTableRows();
                            fetchReadyJobs(true);
                        }}
                        className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-all"
                    >
                        <FiRefreshCw className="w-4 h-4" />
                        Refresh all
                    </button>
                </div>
            </div>

            <ProductionTable
                rows={tableRows}
                onRowsChange={setTableRows}
                isLoading={tableLoading}
                isDragOver={isDragOver}
                setIsDragOver={setIsDragOver}
                onDrop={handleDrop}
                onOpenAssignment={handleOpenAssignment}
                onEditAssignment={handleEditAssignment}
                onStartProduction={handleStartProduction}
                onEndProduction={handleStartProduction}
                userRole={userProfile?.role}
                searchQuery={tableSearch}
                setSearchQuery={setTableSearch}
                quoteTypeFilter={tableQuoteType}
                setQuoteTypeFilter={setTableQuoteType}
                sortBy={tableSortBy}
                setSortBy={setTableSortBy}
                jobTypeFilter={tableJobTypeFilter}
                setJobTypeFilter={setTableJobTypeFilter}
                urgentFilter={tableUrgentFilter}
                setUrgentFilter={setTableUrgentFilter}
                hideCompleted={tableHideCompleted}
                setHideCompleted={setTableHideCompleted}
            />

            <ProductionReadyJobs
                jobs={readyJobs}
                isLoading={readyLoading}
                isRefreshing={readyRefreshing}
                onRefresh={() => fetchReadyJobs(true)}
                onDragStart={handleDragStart}
                onMobileAdd={isMobile && ["app_owner", "admin", "super_admin"].includes(userProfile?.role) ? setMobileProductionJob : undefined}
                searchQuery={readySearch}
                setSearchQuery={setReadySearch}
                quoteTypeFilter={readyQuoteType}
                setQuoteTypeFilter={setReadyQuoteType}
                sortBy={readySortBy}
                setSortBy={setReadySortBy}
                urgentFilter={readyUrgentFilter}
                setUrgentFilter={setReadyUrgentFilter}
            />
            {mobileProductionJob && <MobileScheduleMoveDialog companyState={company?.state} loadCounts={async month => {
                const counts = {};
                const start = new Date(month.getFullYear(), month.getMonth(), 1);
                start.setDate(start.getDate() - (start.getDay() + 6) % 7);
                for (let i = 0; i < 42; i++) { const day = new Date(start); day.setDate(day.getDate() + i); counts[calendarDateKey(day)] = 0; }
                tableRows.forEach(row => { const key = row.scheduledDate?.slice(0, 10); if (key in counts) counts[key]++; });
                return counts;
            }} title="Add to production" submitLabel="Add to production" allowUnscheduled={false}
                jobLabel={mobileProductionJob.childUniqueId || mobileProductionJob.uniqueId}
                onClose={() => setMobileProductionJob(null)} onSelect={date => {
                    const job = mobileProductionJob;
                    setMobileProductionJob(null);
                    handleDrop({ preventDefault() {} }, job, date);
                }} />}
            {/* Edit Assignment Modal */}
            {editOpen && editJobId && (
                <EditAssignment
                    modalJobId={editJobId}
                    modalCompanySlug={companySlug}
                    onClose={() => {
                        setEditOpen(false);
                        setEditJobId(null);
                    }}
                />
            )}
        </div>
    );
};

export default ProductionSchedule;